import { useState } from "react";
import { Header } from "@/components/Header";
import { getBookingByRef } from "@/lib/api";
import { formatDateLong } from "@/lib/format";
import { toast } from "sonner";
import { Search, Printer } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_group-ice-sessions/artifacts/hnppn2we_Sligo%20On%20Ice%20Logo%20-%20Small%20Image.png";
const PRICE_PER_CHILD = 14;

const STATUS_STYLES = {
  submitted: "bg-slate-100 text-slate-900 border-slate-900",
  "in-progress": "bg-amber-100 text-amber-900 border-amber-900",
  confirmed: "bg-sky-500 text-white border-slate-900",
  "invoice sent": "bg-indigo-100 text-indigo-900 border-indigo-900",
  paid: "bg-emerald-500 text-white border-slate-900",
  cancelled: "bg-slate-900 text-white border-slate-900",
};

const formatEuro = (n) => `€${Number(n || 0).toLocaleString("en-IE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function LookupPage() {
  const [ref, setRef] = useState("");
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!ref.trim()) return;
    try {
      setLoading(true);
      const res = await getBookingByRef(ref.trim().toUpperCase());
      setBooking(res);
    } catch (err) {
      setBooking(null);
      toast.error("Booking not found");
    } finally {
      setLoading(false);
    }
  };

  const statusCls = booking ? (STATUS_STYLES[booking.status || "submitted"] || STATUS_STYLES.submitted) : "";
  const cost = booking ? (booking.group_size || 0) * PRICE_PER_CHILD : 0;

  return (
    <div className="min-h-screen bg-slate-50 print-clean">
      <div className="no-print"><Header /></div>
      <div className="max-w-2xl mx-auto px-4 md:px-8 py-16">
        <div className="no-print">
          <div className="font-mono-tight text-xs uppercase tracking-[0.25em] text-slate-500 mb-2">
            Find booking
          </div>
          <h1 className="font-display text-4xl md:text-5xl uppercase mb-8">Lookup by reference</h1>
          <form onSubmit={onSubmit} className="flex gap-3 mb-10" data-testid="lookup-form">
            <input
              value={ref}
              onChange={(e) => setRef(e.target.value)}
              placeholder="SOI-XXXXXXXX"
              className="flex-1 bg-white border-2 border-slate-900 p-3 font-mono-tight uppercase focus:outline-none focus:border-sky-500"
              data-testid="lookup-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="bg-sky-500 text-white font-display uppercase tracking-wide border-2 border-slate-900 brutal-shadow brutal-hover px-6 py-3 flex items-center gap-2 disabled:opacity-60"
              data-testid="lookup-submit"
            >
              <Search className="w-4 h-4" strokeWidth={3} /> Find
            </button>
          </form>
        </div>

        {booking && (
          <>
            <div className="flex justify-end mb-3 no-print">
              <button
                onClick={() => window.print()}
                className="bg-sky-500 text-white font-mono-tight text-xs uppercase tracking-widest border-2 border-slate-900 brutal-shadow brutal-hover px-4 py-2 flex items-center gap-2"
                data-testid="lookup-print"
              >
                <Printer className="w-3.5 h-3.5" strokeWidth={2.5} /> Print / Save PDF
              </button>
            </div>

            <div className="border-2 border-slate-900 bg-white brutal-shadow-lg animate-fade-up print:shadow-none print:border-0" data-testid="lookup-result">
              {/* Header */}
              <div className="flex items-start justify-between p-6 border-b-2 border-slate-900">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 border-2 border-slate-900 overflow-hidden bg-slate-900">
                    <img src={LOGO_URL} alt="Sligo On Ice" className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <div className="font-display text-lg uppercase leading-none">Sligo On Ice</div>
                    <div className="font-mono-tight text-[10px] uppercase tracking-[0.25em] text-slate-500 mt-1">
                      Booking · {booking.reference}
                    </div>
                  </div>
                </div>
                <span
                  className={`inline-block border-2 px-3 py-1 font-mono-tight text-[11px] uppercase tracking-widest ${statusCls}`}
                  data-testid="lookup-status-badge"
                >
                  {booking.status || "submitted"}
                </span>
              </div>

              {/* Session */}
              <div className="p-6 border-b-2 border-slate-900">
                <div className="font-mono-tight text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Session</div>
                <h2 className="font-display text-3xl uppercase">
                  {formatDateLong(booking.date)} · {booking.time}
                </h2>
                <div className="font-mono-tight text-xs text-slate-500 mt-1">45-minute ice session</div>
              </div>

              {/* Details grid */}
              <div className="p-6 grid md:grid-cols-2 gap-5">
                <Info label="School">{booking.school_name}</Info>
                <Info label="School address">{booking.school_address || "—"}</Info>
                <Info label="Teacher">{booking.teacher_name}</Info>
                <Info label="Email">{booking.email}</Info>
                <Info label="Phone">{booking.phone}</Info>
                <Info label="County">{booking.county || "—"}</Info>
                <Info label="Group size">{booking.group_size}</Info>
                <Info label="Age group">{booking.age_group}</Info>
                <Info label="Preferred payment">{booking.payment_method || "—"}</Info>
                <Info label="Estimated cost">
                  <span className="font-bold text-slate-900">{formatEuro(cost)}</span>
                  <span className="text-slate-500 text-xs font-mono-tight ml-2">({booking.group_size} × €14)</span>
                </Info>
                {booking.notes && (
                  <div className="md:col-span-2">
                    <Info label="Notes">{booking.notes}</Info>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-4 border-t-2 border-slate-900 bg-sky-500 text-white font-mono-tight text-[10px] uppercase tracking-widest text-center print:bg-slate-100 print:text-slate-900">
                €14 per child · Teachers &amp; SNAs skate FREE · Any queries email sligoonice@gmail.com
              </div>
            </div>
          </>
        )}
      </div>

      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 14mm; }
          body { background: #fff !important; }
          .no-print { display: none !important; }
          .print-clean { background: #fff !important; }
        }
      `}</style>
    </div>
  );
}

const Info = ({ label, children }) => (
  <div>
    <div className="font-mono-tight text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">{label}</div>
    <div className="text-slate-900">{children}</div>
  </div>
);
